import { loadDurationOptions } from "@/components/reserve/ReserveFieldDuration/helpers";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import {
  DurationOption,
  RESERVE_TEXTS,
  SaveReservationData,
  getDurationOptionFromMinutes
} from "@/lib/utils-reserve";
import { DurationIcon } from "@gettactic/components/src/icons/TacticIcons/DurationIcon";
import { cn } from "@gettactic/helpers";
import { useTimeInputMask } from "@gettactic/helpers/src/input/useTimeInputMask";
import { addDays, addMinutes } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { FieldErrors, UseFormReturn, useWatch } from "react-hook-form";
import { components } from "react-select";
import AsyncSelect from "react-select/async";
import { inputIcon } from "../utils/classes";
import { scrollToSelectedValue } from "../utils/scrollToSelectedValue";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";

type ReserveDurationProps = {
  form: UseFormReturn<SaveReservationData>;
  defaultValue: number;
  setDurationMin: React.Dispatch<React.SetStateAction<number>>;
  errors: FieldErrors<SaveReservationData>;
  durationOptions: DurationOption[];
  startTime: Date;
};

export default function ReserveFieldDuration({
  form,
  defaultValue,
  durationOptions: baseDurationOptions,
  setDurationMin,
  startTime,
  errors
}: ReserveDurationProps) {
  const isMobileApp = isTacticAppWebView();
  const mask = useTimeInputMask();

  const watchedDuration =
    (useWatch({
      control: form.control,
      name: "duration"
    }) as DurationOption | null) ?? null;

  const watchedTimeRaw = useWatch({
    control: form.control,
    name: "time"
  }) as Date | string | null;

  const effectiveStartTime: Date = useMemo(() => {
    const dt =
      watchedTimeRaw instanceof Date
        ? watchedTimeRaw
        : watchedTimeRaw
          ? new Date(watchedTimeRaw)
          : startTime;
    return Number.isNaN(dt.getTime()) ? startTime : dt;
  }, [watchedTimeRaw, startTime]);

  const [selectedOption, setSelectedOption] = useState<DurationOption | null>(
    watchedDuration ??
      (defaultValue
        ? getDurationOptionFromMinutes(effectiveStartTime, defaultValue)
        : null)
  );

  const optionsForStartTime = useMemo(() => {
    if (
      !effectiveStartTime ||
      Number.isNaN(new Date(effectiveStartTime).getTime())
    )
      return [];

    const seeded = loadDurationOptions(
      "",
      effectiveStartTime,
      baseDurationOptions
    );

    const ensureValue = (mins?: number) => {
      if (mins === undefined) return;
      if (!seeded.find((o) => o.value === mins)) {
        seeded.push(getDurationOptionFromMinutes(effectiveStartTime, mins));
      }
    };

    ensureValue(defaultValue);
    ensureValue(watchedDuration?.value ?? undefined);
    ensureValue(selectedOption?.value ?? undefined);

    return seeded.sort((a, b) => a.value - b.value);
  }, [
    effectiveStartTime,
    baseDurationOptions,
    defaultValue,
    watchedDuration?.value,
    watchedTimeRaw
  ]);

  // Keep selection + form value in sync when time changes or when watched duration isn't present
  useEffect(() => {
    let next: DurationOption | null =
      (watchedDuration &&
        (optionsForStartTime.find((o) => o.value === watchedDuration.value) ??
          getDurationOptionFromMinutes(
            effectiveStartTime,
            watchedDuration.value
          ))) ||
      null;

    if (!next && defaultValue) {
      next =
        optionsForStartTime.find((o) => o.value === defaultValue) ??
        getDurationOptionFromMinutes(effectiveStartTime, defaultValue);
    }

    if (next) {
      const maxEnd = addDays(new Date(effectiveStartTime), 1);
      maxEnd.setHours(23, 59, 0, 0);
      const nextEnd = addMinutes(effectiveStartTime, next.value);
      if (nextEnd > maxEnd) {
        const fallback =
          optionsForStartTime
            .filter((o) => addMinutes(effectiveStartTime, o.value) <= maxEnd)
            .sort((a, b) => a.value - b.value)
            .at(-1) ?? null;
        next = fallback;
      }
    }

    const changed =
      (next === null && selectedOption !== null) ||
      (next && (!selectedOption || selectedOption.value !== next.value));

    if (changed) {
      setSelectedOption(next);
      if (next) {
        setDurationMin(next.value);
        form.setValue("duration", next as any, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else {
        form.setValue("duration", null as any, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    }
  }, [
    watchedDuration?.value,
    optionsForStartTime,
    effectiveStartTime,
    defaultValue,
    selectedOption,
    setDurationMin,
    form
  ]);

  // Refresh selection object after options/labels recalc and mirror into form
  useEffect(() => {
    if (!selectedOption) return;
    const refreshed =
      optionsForStartTime.find((o) => o.value === selectedOption.value) ?? null;
    if (
      refreshed &&
      (refreshed.label !== selectedOption.label ||
        refreshed.time !== selectedOption.time)
    ) {
      setSelectedOption(refreshed);
      form.setValue("duration", refreshed as any, {
        shouldDirty: true,
        shouldValidate: false
      });
    }
  }, [optionsForStartTime, selectedOption, form]);

  const handleMenuOpen = () => {
    scrollToSelectedValue(selectedOption);
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (selectedOption) e.currentTarget.value = selectedOption.label;
  };

  return (
    <>
      <FormField
        control={form.control}
        name="duration"
        rules={{ required: true }}
        defaultValue={selectedOption || undefined}
        render={({ field }) => (
          <FormItem>
            <div
              className={cn(
                "[.tactic-v2_&]:focus-ring-rainbow flex-1 [&_.react-select__control]:cursor-pointer [&_.react-select__input-container]:col-span-full [&_.react-select__input]:w-full [&_.react-select__value-container]:grid-cols-1",
                isMobileApp && "v2-mobile-app"
              )}
            >
              <AsyncSelect<DurationOption, false>
                key={`duration-${field.name}`}
                inputId={field.name}
                onFocus={handleFocus}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onMenuOpen={handleMenuOpen}
                isSearchable={isMobileApp ? false : undefined}
                formatOptionLabel={(option) => (
                  <div>
                    <div>{option.label}</div>
                    <div className="text-muted-foreground">
                      {(option as DurationOption).durationLabel}
                    </div>
                  </div>
                )}
                components={{
                  Control: (props) => (
                    <components.Control
                      {...props}
                      className="mt-0 cursor-pointer"
                    />
                  ),
                  SingleValue: (props) => (
                    <components.SingleValue {...props}>
                      {props.data.label}
                    </components.SingleValue>
                  ),
                  Input: (props) => (
                    <components.Input {...props} size={undefined} {...mask} />
                  ),
                  DropdownIndicator: () => (
                    <div className="flex items-center pr-3">
                      <DurationIcon className={cn(inputIcon)} />
                    </div>
                  ),
                  IndicatorSeparator: () => null
                }}
                onChange={(d, event) => {
                  field.onChange(d, event);
                  if (d) {
                    setDurationMin(d.value);
                    setSelectedOption(d);
                    form.setValue("duration", d as any, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  } else {
                    setSelectedOption(null);
                    form.setValue("duration", null as any, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  }
                }}
                value={selectedOption}
                loadOptions={(filter, callback) =>
                  callback(
                    loadDurationOptions(
                      filter,
                      effectiveStartTime,
                      optionsForStartTime
                    )
                  )
                }
                defaultOptions={optionsForStartTime}
                placeholder={RESERVE_TEXTS.selectDuration}
                classNamePrefix="react-select"
              />
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {errors?.duration && (
        <p className="text-primary">Specify a valid duration</p>
      )}
    </>
  );
}
